import { addDays, localDate, weekdayOf, zonedInstant } from '../time/zoned.js';

/**
 * THE SENDING WINDOW (PLAN.md §4.6, M7; owner decisions 2026-10-12). Pure: the pacer enforces it at
 * evaluation and again at the final gate, and the web app uses the very same function for its previews.
 *
 *  - Base rules (SPEC "Sending hours"): campaign and sequence texts Mon–Fri 9:00–19:00, Sat 10:00–14:00,
 *    never on Sundays or federal holidays (the observed date and the actual date); replies and a rep's
 *    one-to-one texts 8:00–21:00 every day. All in the merchant's local time.
 *  - Tightenings (state rules, workspace overrides) are intersected with the base window, so they can
 *    only remove sending time, never add any.
 *  - When the merchant's zone is not certain, every candidate zone counts: a text goes out only when the
 *    window is open in all of them at once (never early, never late anywhere).
 *  - A 2-minute margin at both edges of every window covers clock drift and provider delay: nothing is
 *    handed to the provider in the first or the last 2 minutes.
 *
 * The three required replies never reach this code (apps/worker/src/send/window.ts); caps are separate
 * (caps.ts).
 */

export type MessageClass = 'campaign' | 'reply';

/** Minutes after local midnight. */
export interface DayWindow {
  start: number;
  end: number;
}

const SUNDAY = 0;
const SATURDAY = 6;

/** The base windows from the spec, by weekday (0 = Sunday). */
export function baseWindow(cls: MessageClass, weekday: number): DayWindow | null {
  if (cls === 'reply') return { start: 8 * 60, end: 21 * 60 };
  if (weekday === SUNDAY) return null;
  if (weekday === SATURDAY) return { start: 10 * 60, end: 14 * 60 };
  return { start: 9 * 60, end: 19 * 60 };
}

/** Nothing goes to the provider in the first or last 2 minutes of a window (owner decision). */
export const SEND_MARGIN_MS = 2 * 60_000;

/** How far ahead the next opening is searched. Two weeks always covers a weekend plus holidays. */
const HORIZON_DAYS = 14;

/** One stricter rule (a state rule or a workspace override), in merchant-local minutes. */
export interface Tightening {
  /** Who set it, for people: "Florida", "Workspace: all states". */
  label: string;
  start: number | null;
  end: number | null;
  sundayAllowed: boolean;
  /** A later start on Sundays only (Texas: noon). */
  sundayStart: number | null;
}

export interface WindowInput {
  class: MessageClass;
  /** Candidate IANA zones (at least one). */
  zones: readonly string[];
  tightenings: readonly Tightening[];
  /** Calendar dates (YYYY-MM-DD) closed to campaign-class texts: federal holidays, observed and actual. */
  closedDates: ReadonlySet<string>;
  now: Date;
  /** Defaults to SEND_MARGIN_MS. */
  marginMs?: number;
}

export type WindowClosedReason = 'quiet_hours' | 'holiday' | 'no_window';

export type WindowResult =
  | { open: true; closesAt: Date }
  | { open: false; reason: WindowClosedReason; nextOpen: Date | null };

/** The local window of one calendar date, or null when that day is closed. */
export function dayWindow(
  cls: MessageClass,
  date: string,
  tightenings: readonly Tightening[],
  closedDates: ReadonlySet<string>,
): DayWindow | null {
  const weekday = weekdayOf(date);
  const base = baseWindow(cls, weekday);
  if (!base) return null;
  if (cls === 'campaign' && closedDates.has(date)) return null;
  let { start, end } = base;
  for (const t of tightenings) {
    if (weekday === SUNDAY && !t.sundayAllowed) return null;
    if (t.start !== null) start = Math.max(start, t.start);
    if (weekday === SUNDAY && t.sundayStart !== null) start = Math.max(start, t.sundayStart);
    if (t.end !== null) end = Math.min(end, t.end);
  }
  return start < end ? { start, end } : null;
}

type Interval = [number, number];

/** The open intervals (epoch ms) in one zone, from the day before `now` through the horizon. */
function zoneIntervals(zone: string, input: WindowInput): Interval[] {
  const today = localDate(zone, input.now);
  const out: Interval[] = [];
  for (let i = -1; i <= HORIZON_DAYS; i += 1) {
    const date = addDays(today, i);
    const w = dayWindow(input.class, date, input.tightenings, input.closedDates);
    if (w) {
      out.push([
        zonedInstant(zone, date, w.start).getTime(),
        zonedInstant(zone, date, w.end).getTime(),
      ]);
    }
  }
  return out;
}

function intersect(a: readonly Interval[], b: readonly Interval[]): Interval[] {
  const out: Interval[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    const [as, ae] = a[i] as Interval;
    const [bs, be] = b[j] as Interval;
    const s = Math.max(as, bs);
    const e = Math.min(ae, be);
    if (s < e) out.push([s, e]);
    if (ae < be) i += 1;
    else j += 1;
  }
  return out;
}

/** The open intervals across every candidate zone, with the margin applied. */
export function openIntervals(input: WindowInput): Interval[] {
  if (input.zones.length === 0) throw new RangeError('the sending window needs at least one zone');
  const margin = input.marginMs ?? SEND_MARGIN_MS;
  let acc: Interval[] | null = null;
  for (const zone of new Set(input.zones)) {
    const z = zoneIntervals(zone, input);
    acc = acc === null ? z : intersect(acc, z);
  }
  return (acc ?? []).map(([s, e]): Interval => [s + margin, e - margin]).filter(([s, e]) => s < e);
}

/** Whether a text of this class may go to the provider now, and if not, when it may. */
export function checkSendingWindow(input: WindowInput): WindowResult {
  const now = input.now.getTime();
  const intervals = openIntervals(input);
  const current = intervals.find(([s, e]) => s <= now && now < e);
  if (current) return { open: true, closesAt: new Date(current[1]) };
  const next = intervals.find(([s]) => s > now);
  const holiday =
    input.class === 'campaign' &&
    input.zones.some((z) => input.closedDates.has(localDate(z, input.now)));
  return {
    open: false,
    reason: holiday ? 'holiday' : next ? 'quiet_hours' : 'no_window',
    nextOpen: next ? new Date(next[0]) : null,
  };
}

/** "HH:MM" or "HH:MM:SS" (Postgres `time`) → minutes after midnight; null stays null. */
export function parseTimeOfDay(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const m = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!m) throw new RangeError(`not a time of day: ${value}`);
  const minutes = Number(m[1]) * 60 + Number(m[2]);
  if (minutes > 24 * 60) throw new RangeError(`not a time of day: ${value}`);
  return minutes;
}

/** Minutes after midnight → "HH:MM". */
export function formatTimeOfDay(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}
